import { Alert } from '@creditornot/cb-alert';
import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useTelemetryViewEvent } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { homepageView } from 'core/telemetry/avo/Avo';
import useIsEnterpriseUser from 'modules/enterprise/hooks/useIsEnterpriseUser';

import { FrontPageOnboarding } from './FrontPageOnboarding';
import FrontPageTemplate from './FrontPageTemplate';
import EntitySearch from './Search/EntitySearch';
import { useMerchants } from './hooks/useMerchants';
import { useVenues } from './hooks/useVenues';
import Loading from '../../core/Loading';

const Heading = styled(T.Heading2)`
  color: ${cbSemanticCssVars.textPrimary};
  line-height: 52px;
`;

const Subtitle = styled.p`
  color: ${cbSemanticCssVars.textPrimary};
  line-height: 24px;
`;

const Warning = styled(Alert).attrs({ variant: 'warning' })`
  max-width: 600px;
`;

const FrontPage = () => {
  const { merchants, isLoadingMerchants, merchantsError } = useMerchants();
  const { isEnterpriseUser, isLoading: isLoadingEnterpriseUser } = useIsEnterpriseUser();
  const isVenueFetchEnabled = !isLoadingEnterpriseUser && !isEnterpriseUser;
  const { venues, isLoadingVenues, venuesError } = useVenues({ enabled: isVenueFetchEnabled });
  const isLoading = (isVenueFetchEnabled && isLoadingVenues) || isLoadingMerchants;

  useTelemetryViewEvent('homepage-view', {}, durationMs => homepageView({ durationMs }));

  if (isLoading) {
    return <Loading />;
  }

  if (!merchants || (isVenueFetchEnabled && !venues)) {
    return null;
  }

  return (
    <FrontPageTemplate>
      <Heading>
        <FormattedMessage id="frontpage.header" />
      </Heading>
      <Subtitle>
        <FormattedMessage id="frontpage.description" />
      </Subtitle>
      {merchantsError || venuesError ? (
        <Warning variant="warning">
          <FormattedMessage id="errors.data-fetch-failed-degraded-experience" />
        </Warning>
      ) : (
        <>
          <FrontPageOnboarding merchants={merchants} venues={venues ?? []} />
          <EntitySearch />
        </>
      )}
    </FrontPageTemplate>
  );
};

export default FrontPage;
